import { defineStore } from 'pinia';
import { systemStore } from "@/store/system";

const STORE_NAME = 'createAppointment';
const APPOINT_CHANNEL_DICT_CODE = "HIS_APPOINT_CHANNEL";

/**
 * 获取用于匹配的“客户唯一标识”
 * 后端返回字段不一定统一，这里做兼容。
 */
export function getCreateAppointmentCustomerId(customer = {}) {
  if (!customer) return '';
  return customer?.id || customer?.patientId || customer?.cusId || '';
}

export const createAppointmentStore = defineStore(STORE_NAME, {
  state: () => ({
    /** 当前进入创建预约页面的客户（患者）信息 */
    currentCustomer: {},
    /** 当前患者详情默认对象（用于预约默认值回填） */
    patientDefaultInfo: {},
    /** 预约表单中用户已输入/选择的字段集合 */
    formData: {},
    /** HIS_APPOINT_CHANNEL 结构化后的预约渠道选项 */
    appointChannelOptions: [],
    /** 改约的预约信息 */
    rescheduleAppointmentInfo: {},
  }),
  actions: {
    /**
     * 初始化/切换当前客户（用于后续判断是否需要回填表单）
     * @param {Object} customerData
     */
    setCurrentCustomer(customerData) {
      this.currentCustomer = { ...(customerData || {}) };
    },
    /**
     * 缓存患者详情默认对象（例如默认医生/咨询师）
     * @param {Object} patientData
     */
    setPatientDefaultInfo(patientData) {
      this.patientDefaultInfo = { ...(patientData || {}) };
    },

    /**
     * 保存单个字段（用于更精细的写入）
     * @param {string} key
     * @param {*} value
     */
    saveFormValue(key, value) {
      if (!key) return;
      this.formData = {
        ...(this.formData || {}),
        [key]: value,
      };
    },

    /**
     * 保存一批字段（用于表单输入变更时整体回写）
     * @param {Object} formData
     */
    saveFormData(formData) {
      this.formData = {
        ...(this.formData || {}),
        ...(formData || {}),
      };
    },

    /** 清空表单缓存（不清空 currentCustomer） */
    resetFormData() {
      this.formData = {};
    },

    /**
     * 获取并缓存预约渠道选项，供新建预约页和来源选择复用。
     * @param {boolean} forceRefresh 是否强制重新拉取
     * @returns {Promise<Array>}
     */
    fetchAppointChannelOptions(forceRefresh = false) {
      if (!forceRefresh && this.appointChannelOptions.length > 0) {
        return Promise.resolve(this.appointChannelOptions);
      }

      const store = systemStore();
      return store
        .setBusoptions(APPOINT_CHANNEL_DICT_CODE)
        .then((res) => {
          const appointChannelList = (Array.isArray(res) ? res : [])
            .filter((channelItem) => channelItem && channelItem.value != null)
            .map((channelItem) => ({
              name: channelItem.name || channelItem.value,
              value: channelItem.value,
              rawId: channelItem.item?.id || "",
              item: channelItem.item,
            }));

          this.appointChannelOptions = appointChannelList;
          return appointChannelList;
        })
        .catch((systemError) => {
          console.error("[createAppointmentStore] 获取预约渠道失败", systemError);
          this.appointChannelOptions = [];
          return [];
        });
    },

    /**
     * 缓存改约预约详情（从 getWecomAppointmentDetail 写入）
     * @param {Object} detailData
     */
    setRescheduleAppointmentInfo(detailData) {
      this.rescheduleAppointmentInfo = { ...(detailData || {}) };
    },

    /** 清空改约预约详情 */
    clearRescheduleAppointmentInfo() {
      this.rescheduleAppointmentInfo = {};
    },

    /** 清空 currentCustomer 与表单缓存 */
    resetAll() {
      this.currentCustomer = {};
      this.patientDefaultInfo = {};
      this.formData = {};
      this.rescheduleAppointmentInfo = {};
    },
  },
});